/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * OverviewTab (redesign)
 * Identity + inline-editable models + inline-editable system prompt.
 * No more popup for fields that clearly belong on the page itself — the
 * prompt and model pickers save in place.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Check as SaveIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as CapabilityIcon,
  Description as PromptIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Info as InfoIcon,
  Psychology as BrainIcon,
  SmartToy as ModelIcon,
} from '@mui/icons-material';
import Editor, { type Monaco } from '@monaco-editor/react';

import type { AgentDetail as AgentDetailType } from '../../../types';
import { embeddingModelApi, llmModelApi } from '../../../services/api';
import { formatStoredDateTime } from '../../../utils/datetime';
import { SectionCard, CopyButton, KeyValue } from './primitives';
import { TemplateInfoCard } from '../TemplateInfoCard';

interface OverviewTabProps {
  agent: AgentDetailType;
  saving: boolean;
  /** Partial save — caller ensures model_id + system_prompt are set for the serializer */
  onSavePartial: (data: Record<string, any>) => Promise<void>;
  /** Full edit dialog — still used for name / description / type / flags */
  onOpenFullEdit: () => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  agent,
  saving,
  onSavePartial,
  onOpenFullEdit,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // ---------------------------------------------------------------- prompt
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [promptEditing, setPromptEditing] = useState(false);
  const [promptFullscreen, setPromptFullscreen] = useState(false);
  const [promptDraft, setPromptDraft] = useState(agent.system_prompt || '');
  const promptEditorRef = useRef<any>(null);

  useEffect(() => {
    if (!promptEditing) setPromptDraft(agent.system_prompt || '');
  }, [agent.system_prompt, promptEditing]);

  const prompt = agent.system_prompt || '';
  const lineCount = prompt ? prompt.split('\n').length : 0;
  const charCount = prompt.length;
  const previewLines = 12;
  const previewPrompt = prompt.split('\n').slice(0, previewLines).join('\n');
  const hasMore = lineCount > previewLines || charCount > 800;
  const promptDirty = promptDraft !== (agent.system_prompt || '');

  const handleStartPromptEdit = () => {
    setPromptDraft(agent.system_prompt || '');
    setPromptEditing(true);
  };
  const handleCancelPrompt = () => {
    setPromptDraft(agent.system_prompt || '');
    setPromptEditing(false);
    setPromptFullscreen(false);
  };
  const handleSavePrompt = async () => {
    try {
      await onSavePartial({ system_prompt: promptDraft });
      setPromptEditing(false);
      setPromptFullscreen(false);
    } catch {
      /* toast handled by caller */
    }
  };

  // ---------------------------------------------------------------- models
  const [modelEditing, setModelEditing] = useState(false);
  const [modelDraft, setModelDraft] = useState<number>(agent.model?.id || 0);
  const supportsHiddenBuilderModel = agent.agent_type === 'AI_SYSTEM_BUILDER';
  const currentBuilderModelId = agent.builder_model?.id ?? agent.builder_model_id ?? '';
  const [builderModelDraft, setBuilderModelDraft] = useState<number | ''>(currentBuilderModelId);
  const currentEmbeddingId = agent.embedding_model?.id ?? agent.embedding_model_id ?? '';
  const [embeddingDraft, setEmbeddingDraft] = useState<number | ''>(currentEmbeddingId);

  useEffect(() => {
    if (!modelEditing) {
      setModelDraft(agent.model?.id || 0);
      setBuilderModelDraft(agent.builder_model?.id ?? agent.builder_model_id ?? '');
      setEmbeddingDraft(agent.embedding_model?.id ?? agent.embedding_model_id ?? '');
    }
  }, [
    agent.model?.id,
    agent.builder_model?.id,
    agent.builder_model_id,
    agent.embedding_model?.id,
    agent.embedding_model_id,
    modelEditing,
  ]);

  const { data: llmModels, isLoading: llmLoading } = useQuery({
    queryKey: ['llm-models'],
    queryFn: () => llmModelApi.list({ is_active: true }),
    enabled: modelEditing,
  });
  const { data: embeddingModels, isLoading: embLoading } = useQuery({
    queryKey: ['embedding-models'],
    queryFn: () => embeddingModelApi.list({ is_active: true }),
    enabled: modelEditing,
  });

  const modelsDirty =
    modelDraft !== (agent.model?.id || 0) ||
    (supportsHiddenBuilderModel && builderModelDraft !== currentBuilderModelId) ||
    embeddingDraft !== currentEmbeddingId;

  const handleSaveModels = async () => {
    try {
      await onSavePartial({
        model_id: modelDraft,
        ...(supportsHiddenBuilderModel
          ? { builder_model_id: builderModelDraft === '' ? null : builderModelDraft }
          : {}),
        embedding_model_id: embeddingDraft === '' ? null : embeddingDraft,
      });
      setModelEditing(false);
    } catch {
      /* noop */
    }
  };
  const handleCancelModels = () => {
    setModelDraft(agent.model?.id || 0);
    setBuilderModelDraft(currentBuilderModelId);
    setEmbeddingDraft(currentEmbeddingId);
    setModelEditing(false);
  };

  // ----------------------------------------------------------------- render
  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }}>
      {agent.template_info?.is_template_based ? (
        <TemplateInfoCard templateInfo={agent.template_info} />
      ) : null}

      {/* ---------- Identity ---------- */}
      <SectionCard
        icon={<InfoIcon fontSize="small" />}
        title="Identity"
        actions={
          <Button size="small" startIcon={<EditIcon />} onClick={onOpenFullEdit}>
            Edit details
          </Button>
        }
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KeyValue label="Name" value={agent.name} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KeyValue
              label="Status"
              value={
                <Chip
                  size="small"
                  label={agent.is_active ? 'Active' : 'Inactive'}
                  color={agent.is_active ? 'success' : 'default'}
                />
              }
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <KeyValue
              label="Description"
              value={agent.description || <em style={{ opacity: 0.7 }}>No description</em>}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KeyValue label="Created" value={formatStoredDateTime(agent.created_at, undefined, '-')} />
          </Grid>
          {agent.updated_at ? (
            <Grid size={{ xs: 12, sm: 6 }}>
              <KeyValue
                label="Last updated"
                value={formatStoredDateTime(agent.updated_at, undefined, '-')}
              />
            </Grid>
          ) : null}
        </Grid>
      </SectionCard>

      {/* ---------- Models (inline edit) ---------- */}
      <SectionCard
        icon={<ModelIcon fontSize="small" />}
        title="Models"
        description="The LLM that drives this agent and the embedding model used for memory search."
        actions={
          modelEditing ? (
            <Stack direction="row" spacing={0.5}>
              <Button size="small" onClick={handleCancelModels} disabled={saving}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveModels}
                disabled={saving || !modelDraft || !modelsDirty}
              >
                Save
              </Button>
            </Stack>
          ) : (
            <Button size="small" startIcon={<EditIcon />} onClick={() => setModelEditing(true)}>
              Change
            </Button>
          )
        }
      >
        {modelEditing ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{supportsHiddenBuilderModel ? 'Architect model' : 'LLM model'}</InputLabel>
                <Select
                  label={supportsHiddenBuilderModel ? 'Architect model' : 'LLM model'}
                  value={modelDraft || ''}
                  onChange={(e) => setModelDraft(Number(e.target.value))}
                  disabled={llmLoading || saving}
                >
                  {llmLoading ? (
                    <MenuItem value="">Loading…</MenuItem>
                  ) : (
                    (llmModels?.results || []).map((m: any) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          · {m.provider_name || 'Unknown'}
                        </Typography>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            {supportsHiddenBuilderModel ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Hidden Builder model</InputLabel>
                  <Select
                    label="Hidden Builder model"
                    value={builderModelDraft}
                    onChange={(e) =>
                      setBuilderModelDraft(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    disabled={llmLoading || saving}
                  >
                    <MenuItem value="">
                      <em>Use Architect model</em>
                    </MenuItem>
                    {(llmModels?.results || []).map((m: any) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          · {m.provider_name || 'Unknown'}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Embedding model</InputLabel>
                <Select
                  label="Embedding model"
                  value={embeddingDraft}
                  onChange={(e) =>
                    setEmbeddingDraft(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  disabled={embLoading || saving}
                >
                  <MenuItem value="">
                    <em>Use provider or agent defaults</em>
                  </MenuItem>
                  {(embeddingModels?.results || []).map((m: any) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0.5 }}
                      >
                        · {m.provider_name || 'Unknown'}
                        {m.dimensions ? `, ${m.dimensions}d` : ''}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <KeyValue
                label={supportsHiddenBuilderModel ? 'Architect model' : 'LLM model'}
                value={
                  agent.model?.name ? (
                    <>
                      {agent.model.name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        · {agent.model.provider_name || 'Unknown provider'}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Not configured
                    </Typography>
                  )
                }
              />
            </Grid>
            {supportsHiddenBuilderModel ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <KeyValue
                  label="Hidden Builder model"
                  value={
                    agent.builder_model?.name ? (
                      <>
                        {agent.builder_model.name}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          · {agent.builder_model.provider_name || 'Unknown provider'}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Using Architect model
                      </Typography>
                    )
                  }
                />
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, md: 6 }}>
              <KeyValue
                label="Embedding model"
                value={
                  agent.embedding_model?.name ? (
                    <>
                      {agent.embedding_model.name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        · {agent.embedding_model.provider_name || 'Unknown'}
                        {agent.embedding_model.dimensions
                          ? `, ${agent.embedding_model.dimensions}d`
                          : ''}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Using provider default
                    </Typography>
                  )
                }
              />
            </Grid>
          </Grid>
        )}
      </SectionCard>

      {/* ---------- Capabilities ---------- */}
      <SectionCard
        icon={<CapabilityIcon fontSize="small" />}
        title="Capabilities"
        description="Tags describing what this agent can do. Used to filter and route."
      >
        {agent.capabilities && agent.capabilities.length > 0 ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {agent.capabilities.map((cap, idx) => (
              <Chip
                key={`${cap}-${idx}`}
                label={cap}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No capabilities defined.
          </Typography>
        )}
      </SectionCard>

      {/* ---------- System Prompt (inline Monaco editor) ---------- */}
      <SectionCard
        icon={<PromptIcon fontSize="small" />}
        title="System prompt"
        description={
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <span>Defines the agent's role, tone and behaviour.</span>
            <Typography variant="caption" color="text.secondary">
              {(promptEditing ? promptDraft.split('\n').length : lineCount).toLocaleString()} lines ·{' '}
              {(promptEditing ? promptDraft.length : charCount).toLocaleString()} chars
              {promptEditing && promptDirty ? ' · unsaved' : ''}
            </Typography>
          </Stack>
        }
        actions={
          promptEditing ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={promptFullscreen ? 'Exit fullscreen' : 'Fullscreen editor'}>
                <IconButton
                  size="small"
                  onClick={() => setPromptFullscreen((v) => !v)}
                  aria-label="toggle fullscreen"
                >
                  {promptFullscreen ? (
                    <FullscreenExitIcon fontSize="small" />
                  ) : (
                    <FullscreenIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                startIcon={<CancelIcon />}
                onClick={handleCancelPrompt}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePrompt}
                disabled={saving || !promptDirty || !promptDraft.trim()}
              >
                Save prompt
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5}>
              <CopyButton value={prompt} label="System prompt" title="Copy prompt" />
              <Button size="small" startIcon={<EditIcon />} onClick={handleStartPromptEdit}>
                Edit prompt
              </Button>
            </Stack>
          )
        }
      >
        {promptEditing ? (
          <Box
            sx={{
              borderRadius: 1,
              border: 2,
              borderColor: 'primary.main',
              overflow: 'hidden',
              position: promptFullscreen ? 'fixed' : 'relative',
              inset: promptFullscreen ? 16 : 'auto',
              zIndex: promptFullscreen ? (t) => t.zIndex.modal + 1 : 'auto',
              bgcolor: 'background.paper',
              boxShadow: promptFullscreen ? 24 : 'none',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {promptFullscreen ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Editing system prompt · {agent.name}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Button size="small" onClick={handleCancelPrompt} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSavePrompt}
                    disabled={saving || !promptDirty || !promptDraft.trim()}
                  >
                    Save prompt
                  </Button>
                  <IconButton size="small" onClick={() => setPromptFullscreen(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            ) : null}
            <Box sx={{ flex: 1, minHeight: promptFullscreen ? 0 : 420 }}>
              <Editor
                height={promptFullscreen ? '100%' : '420px'}
                defaultLanguage="markdown"
                value={promptDraft}
                onChange={(v) => setPromptDraft(v || '')}
                onMount={(editor, _monaco: Monaco) => {
                  promptEditorRef.current = editor;
                  setTimeout(() => editor.focus(), 50);
                }}
                theme={isDark ? 'vs-dark' : 'vs-light'}
                options={{
                  minimap: { enabled: false },
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  padding: { top: 12, bottom: 12 },
                  automaticLayout: true,
                }}
              />
            </Box>
            {saving ? <LinearProgress /> : null}
          </Box>
        ) : prompt ? (
          <Box
            sx={{
              position: 'relative',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              bgcolor: isDark ? alpha('#000', 0.25) : alpha('#000', 0.02),
            }}
          >
            <Collapse in={promptExpanded} collapsedSize={hasMore ? 220 : 'auto'}>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.82rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.55,
                }}
              >
                {promptExpanded ? prompt : previewPrompt}
                {!promptExpanded && hasMore ? '\n…' : ''}
              </Box>
            </Collapse>
            {hasMore ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.background.default, 0.6),
                }}
              >
                <Button
                  size="small"
                  onClick={() => setPromptExpanded((v) => !v)}
                  startIcon={promptExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ width: '100%', borderRadius: 0 }}
                >
                  {promptExpanded ? 'Show less' : 'Show full prompt'}
                </Button>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Stack
            alignItems="flex-start"
            spacing={1}
            sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderStyle: 'dashed',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No system prompt configured yet.
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleStartPromptEdit}
            >
              Write system prompt
            </Button>
          </Stack>
        )}
      </SectionCard>

      {/* ---------- Usage context ---------- */}
      <SectionCard icon={<BrainIcon fontSize="small" />} title="Usage context">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KeyValue
              label="Usage context"
              value={agent.config?.usage_context || 'General purpose agent'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KeyValue
              label="Flavour"
              value={
                <Chip
                  size="small"
                  label={agent.config?.agent_type || 'conversational'}
                  color="info"
                  variant="outlined"
                />
              }
            />
          </Grid>
          {typeof agent.tool_count === 'number' ? (
            <Grid size={{ xs: 12, sm: 6 }}>
              <KeyValue label="Tools attached" value={agent.tool_count} />
            </Grid>
          ) : null}
          {agent.template_info?.last_synced_at ? (
            <Grid size={{ xs: 12, sm: 6 }}>
              <KeyValue
                label="Template last synced"
                value={formatStoredDateTime(agent.template_info.last_synced_at, undefined, '-')}
              />
            </Grid>
          ) : null}
        </Grid>
      </SectionCard>
    </Stack>
  );
};

export default OverviewTab;
